import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { getTeachersBySchoolId, updateTeacherProfileWithEmail } from "../services/TeacherAPIClient";
import "./TeacherRenameUserModal.css";

const reasonRows = [
  { value: "Name changed", helper: "For example, marriage or legal name change" },
  { value: "Spelling correction", helper: "The current name is incorrect" },
  { value: "Other reason", helper: "Add a short note" }
];

const splitName = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] || "", lastName: parts.slice(1).join(" ") };
};

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const getTeacherId = (teacher) =>
  teacher?.id || teacher?._id || teacher?.teacherid || teacher?.teacherId || teacher?.teacher_id;

const normaliseTeacher = (teacher) => {
  const name =
    teacher?.name ||
    teacher?.fullName ||
    teacher?.displayName ||
    [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ") ||
    "Unnamed teacher";

  return {
    ...teacher,
    id: getTeacherId(teacher),
    name,
    email: teacher?.email || "",
    helper: teacher?.email || teacher?.roleLabel || teacher?.role || "Current name on account"
  };
};

const findSingleExactTeacher = (teachers, teacherName) => {
  const target = normaliseText(teacherName);

  if (!target) {
    return null;
  }

  const matches = teachers.filter((teacher) => normaliseText(teacher.name) === target);
  return matches.length === 1 ? matches[0] : null;
};

const buildTeacherProfilePayload = (teacher, name) => {
  const payload = {
    name,
    email: teacher.email
  };

  if (teacher.mobileNumber) {
    payload.mobileNumber = teacher.mobileNumber;
  }

  if (Array.isArray(teacher.additionalRoles) && teacher.additionalRoles.length > 0) {
    payload.additionalRoles = teacher.additionalRoles;
  }

  return payload;
};

function TeacherRenameUserModal({
  show,
  handleClose,
  schoolid,
  currentTeacherName,
  currentTeacherEmail = "",
  prefill = null,
  onUpdateUserEmail = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [renameTarget, setRenameTarget] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [teacherName, setTeacherName] = useState("");
  const [selectedTeacherId, setSelectedTeacherId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [reason, setReason] = useState("");
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    const nameParts = splitName(prefill?.newTeacherName);
    const nextTeacherName = prefill?.teacherName || "";

    setStage("form");
    setRenameTarget(prefill?.teacherName ? "A colleague's name" : "");
    setTeachers([]);
    setTeacherName(nextTeacherName);
    setSelectedTeacherId("");
    setFirstName(prefill?.firstName || nameParts.firstName || "");
    setLastName(prefill?.lastName || nameParts.lastName || "");
    setReason(prefill?.reason || "");
    setIsSubmitting(false);
    setError("");

    if (!schoolid) {
      setError("Teachers could not be loaded. Please try again.");
      return;
    }

    setIsLoadingTeachers(true);
    getTeachersBySchoolId(schoolid)
      .then((teachersResponse) => {
        const normalisedTeachers = (Array.isArray(teachersResponse) ? teachersResponse : [])
          .map(normaliseTeacher)
          .filter((teacher) => teacher.id && teacher.active !== false)
          .sort((firstTeacher, secondTeacher) => firstTeacher.name.localeCompare(secondTeacher.name));
        const prefilledTeacher = findSingleExactTeacher(normalisedTeachers, nextTeacherName);

        setTeachers(normalisedTeachers);
        setSelectedTeacherId(prefilledTeacher?.id || "");
      })
      .catch((loadError) => {
        console.error("Error loading rename-user teachers:", loadError);
        setError("Teachers could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoadingTeachers(false);
      });
  }, [show, schoolid, prefill]);

  const selectedTeacher = renameTarget === "My name" ? currentTeacherName || teacherName : teacherName;
  const newName = `${firstName} ${lastName}`.trim();
  const selectedTargetTeacher = useMemo(() => {
    if (renameTarget === "My name") {
      return teachers.find((teacher) =>
        (currentTeacherEmail && normaliseText(teacher.email) === normaliseText(currentTeacherEmail)) ||
        normaliseText(teacher.name) === normaliseText(currentTeacherName || teacherName)
      ) || null;
    }

    if (selectedTeacherId) {
      return teachers.find((teacher) => teacher.id === selectedTeacherId) || null;
    }

    return findSingleExactTeacher(teachers, teacherName);
  }, [currentTeacherEmail, currentTeacherName, renameTarget, selectedTeacherId, teacherName, teachers]);
  const unchanged = Boolean(selectedTeacher.trim() && newName) && selectedTeacher.trim().toLowerCase() === newName.toLowerCase();
  const canContinue = Boolean(
    selectedTeacher.trim() &&
    selectedTargetTeacher &&
    firstName.trim() &&
    lastName.trim() &&
    reason.trim() &&
    !unchanged &&
    !isSubmitting
  );
  const filteredTeachers = useMemo(() => {
    const query = normaliseText(teacherName);

    if (!query || renameTarget === "My name") {
      return [];
    }

    return teachers.filter((teacher) =>
      [teacher.name, teacher.email, teacher.helper].some((value) =>
        normaliseText(value).includes(query)
      )
    );
  }, [renameTarget, teacherName, teachers]);

  const selectTeacher = (teacher) => {
    setSelectedTeacherId(teacher.id);
    setTeacherName(teacher.name);
    setError("");
  };

  const resetForAnother = () => {
    setStage("form");
    setRenameTarget("");
    setTeacherName("");
    setSelectedTeacherId("");
    setFirstName("");
    setLastName("");
    setReason("");
    setIsSubmitting(false);
    setError("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: selectedTargetTeacher?.email || selectedTeacher || "",
        message: "Teacher rename request sent."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const handleConfirmNameChange = async () => {
    if (!canContinue || !selectedTargetTeacher) {
      setError("Choose a teacher from the results before continuing.");
      setStage("form");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await updateTeacherProfileWithEmail(
        schoolid,
        selectedTargetTeacher.id,
        buildTeacherProfilePayload(selectedTargetTeacher, newName)
      );
      setStage("success");
    } catch (submitError) {
      console.error("Error renaming teacher:", submitError);
      setError("The teacher name could not be updated. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderOptionCard = (value, helper, selected, onSelect) => (
    <button
      key={value}
      type="button"
      className={`rename-user-option${selected ? " selected" : ""}`}
      onClick={() => onSelect(value)}
    >
      <span className="rename-user-radio" aria-hidden="true" />
      <span>
        <strong>{value}</strong>
        <span>{helper}</span>
      </span>
    </button>
  );

  const renderTeacherResults = () => {
    if (renameTarget === "My name") {
      return <p className="rename-user-empty">Your signed-in teacher name is selected.</p>;
    }

    if (isLoadingTeachers) {
      return (
        <div className="rename-user-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading teachers...</span>
        </div>
      );
    }

    if (!teacherName.trim()) {
      return <p className="rename-user-empty">Start typing a teacher name.</p>;
    }

    if (filteredTeachers.length < 1) {
      return <p className="rename-user-empty">No matching teachers found.</p>;
    }

    return filteredTeachers.slice(0, 5).map((teacher) => (
      <button
        key={teacher.id}
        type="button"
        className={`rename-user-result${selectedTeacherId === teacher.id ? " selected" : ""}`}
        onClick={() => selectTeacher(teacher)}
        aria-label={`Select ${teacher.name}`}
        aria-pressed={selectedTeacherId === teacher.id}
      >
        <span className="rename-user-radio" aria-hidden="true" />
        <span>
          <strong>{teacher.name}</strong>
          <span>{teacher.helper}</span>
        </span>
      </button>
    ));
  };

  return (
    <Modal show={show} onHide={handleModalHide} centered backdrop="static" className="teacher-rename-user-modal">
      {stage === "form" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Update Teacher Name</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="rename-user-intro">Choose whether this is your name or a colleague's name.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            {unchanged && <Alert variant="danger">Enter a different new name before continuing.</Alert>}

            <Form.Group>
              <Form.Label>Whose name is changing?</Form.Label>
              <div className="rename-user-options">
                {renderOptionCard("A colleague's name", "Search for a teacher", renameTarget === "A colleague's name", setRenameTarget)}
                {renderOptionCard("My name", "Change my displayed name", renameTarget === "My name", setRenameTarget)}
              </div>
            </Form.Group>

            <div className="rename-user-section">
              <Form.Label as="div">Which teacher needs a name update?</Form.Label>
              <Form.Group controlId="renameUserTeacher">
                <Form.Label className="rename-user-field-label">Search teacher</Form.Label>
                <Form.Control
                  value={selectedTeacher}
                  onChange={(event) => {
                    setTeacherName(event.target.value);
                    setSelectedTeacherId("");
                    setError("");
                  }}
                  disabled={renameTarget === "My name" && Boolean(currentTeacherName)}
                />
                <Form.Text className="text-muted">Start typing a teacher name.</Form.Text>
              </Form.Group>
              <div className="rename-user-results">
                {renderTeacherResults()}
              </div>
            </div>

            <div className="rename-user-section">
              <Form.Label as="div">Correct name</Form.Label>
              <Form.Group className="mb-3" controlId="renameUserFirst">
                <Form.Label className="rename-user-field-label">First name</Form.Label>
                <Form.Control value={firstName} onChange={(event) => setFirstName(event.target.value)} />
              </Form.Group>
              <Form.Group controlId="renameUserLast">
                <Form.Label className="rename-user-field-label">Last name</Form.Label>
                <Form.Control value={lastName} onChange={(event) => setLastName(event.target.value)} />
              </Form.Group>
            </div>

            <Form.Group className="rename-user-section">
              <Form.Label>Reason</Form.Label>
              <div className="rename-user-options">
                {reasonRows.map((row) => renderOptionCard(row.value, row.helper, reason === row.value, setReason))}
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <span className="rename-user-footer-note">
              {canContinue ? `${selectedTeacher} will become ${newName}.` : "Complete the name change."}
            </span>
            <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
            <Button variant="primary" onClick={() => setStage("confirm")} disabled={!canContinue}>Next</Button>
          </Modal.Footer>
        </>
      )}
      {stage === "confirm" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Confirm Name Change</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="rename-user-intro">Check the new name before saving.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            <Alert variant="warning" className="rename-user-warning">
              <strong>Rename {selectedTeacher} to {newName}?</strong>
              <span>This changes the displayed name only. Email, role, and class access stay the same.</span>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <span className="rename-user-footer-note">{isSubmitting ? "Updating name..." : "Ready to update name."}</span>
            <Button variant="outline-secondary" onClick={() => setStage("form")} disabled={isSubmitting}>Back</Button>
            <Button variant="primary" onClick={handleConfirmNameChange} disabled={isSubmitting}>
              {isSubmitting ? "Updating..." : "Update name"}
            </Button>
          </Modal.Footer>
        </>
      )}
      {stage === "success" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Name Updated</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="rename-user-intro">The teacher's email, role, and class access were not changed.</p>
            <Alert variant="success" className="rename-user-success">
              <span className="rename-user-ok-badge" aria-hidden="true">OK</span>
              <span>
                <strong>{selectedTeacher} is now shown as {newName}.</strong>
                <span>The teacher's email, role, and class access were not changed.</span>
              </span>
            </Alert>
            <div className="rename-user-next">
              <strong>What next?</strong>
              <span>
                {embedded
                  ? "Done returns to SENSAI. Rename another teacher repeats the action."
                  : "Done returns to Help Centre home. Rename another teacher repeats the action."}
              </span>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <span className="rename-user-footer-note">Task complete.</span>
            <Button variant="outline-secondary" onClick={resetForAnother}>Rename another teacher</Button>
            {onUpdateUserEmail && (
              <Button
                variant="outline-secondary"
                onClick={() => onUpdateUserEmail({
                  teacherName: newName,
                  currentEmail: selectedTargetTeacher?.email
                })}
              >
                Update user email
              </Button>
            )}
            <Button variant="primary" onClick={handleDone}>Done</Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

export default TeacherRenameUserModal;
